import { PublicationReleaseApiError } from "./publication-release-api";
import { getStudioPublishDeployCopy } from "@/i18n/studio-publish-deploy-copy";
import {
  DEFAULT_LOCALE,
  type LocaleCode,
} from "@/pages/cogo-marketing/i18n/locales";

/**
 * The one spelling of the target-profile catalog failure, shared by its
 * emitter (`use-publication-release.ts`) and its reader (the classifier
 * below). It used to live twice — minted as English prose in the hook and
 * matched as an English prefix here — so rewording the sentence would have
 * silently downgraded the error to `unknown` and lost its recovery guidance.
 * Write-side rule: the consumer derives its gate from the producer's name.
 */
export const TARGET_PROFILE_CATALOG_UNAVAILABLE =
  "Target profile catalog unavailable";

export type PublicationReleaseErrorKind =
  | "authentication"
  | "permission"
  | "timeout"
  | "throttled"
  | "conflict"
  | "network"
  | "server"
  | "validation"
  | "catalog"
  | "unknown";

export interface PublicationReleaseErrorPresentation {
  kind: PublicationReleaseErrorKind;
  title: string;
  message: string;
  recovery: string;
  refreshFirst: boolean;
}

/**
 * Convert transport and controller errors into bounded recovery guidance.
 * In particular, timeout/network/server failures never invite a new external
 * effect first: the existing release must be read back because the provider
 * may already have accepted the idempotent request.
 */
export function presentPublicationReleaseError(
  cause: unknown,
  locale: LocaleCode = DEFAULT_LOCALE,
): PublicationReleaseErrorPresentation {
  const copy = getStudioPublishDeployCopy(locale).release.errorBanner;
  const message = errorMessage(cause, copy.defaultMessage);
  const normalized = message.toLowerCase();
  const status =
    cause instanceof PublicationReleaseApiError
      ? cause.status
      : structuralStatus(cause);
  const code =
    cause instanceof PublicationReleaseApiError
      ? cause.code?.toLowerCase()
      : structuralCode(cause);

  // Exact transport stub `Not found` is not a log line an operator can grep.
  // The publication panel stores `cause.message` as a string (no HTTP status),
  // so this must match before the kind cascade. Studio already authors
  // `defaultMessage` for a cause with no usable text; reuse that leaf instead
  // of rendering the Latin stub into the candidate/launch banner.
  if (normalized === "not found") {
    return {
      kind: "validation",
      title: copy.kinds.validation.title,
      message: copy.defaultMessage,
      recovery: copy.kinds.validation.recovery,
      refreshFirst: false,
    };
  }

  if (
    status === 401 ||
    normalized.includes("not authenticated") ||
    normalized.includes("authentication required") ||
    code === "unauthenticated"
  ) {
    return {
      kind: "authentication",
      title: copy.kinds.authentication.title,
      message,
      recovery: copy.kinds.authentication.recovery,
      refreshFirst: true,
    };
  }

  if (
    status === 403 ||
    normalized.includes("forbidden") ||
    normalized.includes("permission denied") ||
    code === "forbidden"
  ) {
    return {
      kind: "permission",
      title: copy.kinds.permission.title,
      message,
      recovery: copy.kinds.permission.recovery,
      refreshFirst: true,
    };
  }

  if (
    status === 408 ||
    normalized.includes("timed out") ||
    normalized.includes("timeout") ||
    normalized.includes("status confirmation") ||
    code === "request_timeout"
  ) {
    return {
      kind: "timeout",
      title: copy.kinds.timeout.title,
      message,
      recovery: copy.kinds.timeout.recovery,
      refreshFirst: true,
    };
  }

  if (
    status === 425 ||
    status === 429 ||
    normalized.includes("rate limit") ||
    normalized.includes("throttl") ||
    code === "too_many_requests"
  ) {
    return {
      kind: "throttled",
      title: copy.kinds.throttled.title,
      message,
      recovery: copy.kinds.throttled.recovery,
      refreshFirst: true,
    };
  }

  if (
    status === 409 ||
    status === 412 ||
    normalized.includes("stale") ||
    normalized.includes("conflict") ||
    code === "projection_conflict"
  ) {
    return {
      kind: "conflict",
      title: copy.kinds.conflict.title,
      message,
      recovery: copy.kinds.conflict.recovery,
      refreshFirst: true,
    };
  }

  if (
    normalized.startsWith(TARGET_PROFILE_CATALOG_UNAVAILABLE.toLowerCase()) ||
    code === "target_profile_catalog_unavailable"
  ) {
    // §F7 — heading and recovery localized because they come from the copy
    // table; the BODY arrived pre-minted in English from
    // `use-publication-release.ts` and was passed through verbatim, so the ko
    // appliance read `Target profile catalog unavailable: No route matched
    // /api/publication-target-profiles` inside an otherwise Korean panel.
    // The cause detail survives — localizing is not swallowing.
    const detail = message.slice(TARGET_PROFILE_CATALOG_UNAVAILABLE.length + 2);
    return {
      kind: "catalog",
      title: copy.kinds.catalog.title,
      message: copy.catalogMessage(detail.trim() || message),
      recovery: copy.kinds.catalog.recovery,
      refreshFirst: true,
    };
  }

  if (status !== undefined && status >= 500) {
    return {
      kind: "server",
      title: copy.kinds.server.title,
      message,
      recovery: copy.kinds.server.recovery,
      refreshFirst: true,
    };
  }

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("network") ||
    normalized.includes("offline")
  ) {
    return {
      kind: "network",
      title: copy.kinds.network.title,
      message,
      recovery: copy.kinds.network.recovery,
      refreshFirst: true,
    };
  }

  if (
    status === 400 ||
    status === 404 ||
    status === 422 ||
    normalized.includes("requires") ||
    normalized.includes("missing") ||
    normalized.includes("invalid")
  ) {
    return {
      kind: "validation",
      title: copy.kinds.validation.title,
      message,
      recovery: copy.kinds.validation.recovery,
      refreshFirst: false,
    };
  }

  return {
    kind: "unknown",
    title: copy.kinds.unknown.title,
    message,
    recovery: copy.kinds.unknown.recovery,
    refreshFirst: true,
  };
}

function errorMessage(value: unknown, fallback: string): string {
  if (value instanceof Error && value.message.trim()) return value.message;
  if (typeof value === "string" && value.trim()) return value.trim();
  return fallback;
}

function structuralStatus(value: unknown): number | undefined {
  if (!value || typeof value !== "object") return undefined;
  const status = (value as { status?: unknown }).status;
  return typeof status === "number" && Number.isInteger(status)
    ? status
    : undefined;
}

function structuralCode(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const code = (value as { code?: unknown }).code;
  return typeof code === "string" ? code.toLowerCase() : undefined;
}
